import { useQuery } from 'react-query';
import apiClient from '../apiClient/apiClient';

// Types for the products API response
export interface ProductVariant {
  variantId: string;
  modifiable: boolean;
  maxAllowedModifierWeight: number;
  brandTags: string[];
  promoTags: string[];
  name: string;
  variantCode: string;
  price: {
    amount: number;
    currencyCode: string;
  };
  authoringPrice: {
    amount: number;
    namedPrice: {
      name: string;
      amount: number;
    };
  };
  selectedOptions: Array<{
    optionType: {
      optionTypeId: string;
      optionTypeCode: string;
      internalName: string;
    };
    optionValue: {
      optionValueId: string;
      optionValueCode: string;
      internalName: string;
    };
  }>;
}

export interface ProductCategory {
  categoryId: string;
  internalName: string;
  name: string;
  description: string;
}

export interface Product {
  productId: string;
  name: string;
  internalName: string;
  price: number;
  productCode: string;
  categories: ProductCategory[];
  variants: ProductVariant[];
  modifiers: any;
  options: any;
  createdAt: string;
  updatedAt: string;
  promoTags?: string[];
}

export interface ProductsResponse {
  count: number;
  page: number;
  pages: number;
  products: Product[];
}

export interface VariantInfo {
  variantId: string;
  productName: string;
  productInternalName: string;
  variantDescription: string;
  fullDescription: string;
  price: number;
  categories: ProductCategory[];
  options: Array<{
    optionType: string;
    optionValue: string;
  }>;
}

// Fetch all products (we'll need to handle pagination)
export const useProducts = (page: number = 0, pageSize: number = 100) => {
  return useQuery<ProductsResponse>(
    ['products', page, pageSize],    async () => {
      const response = await apiClient.get<ProductsResponse>('/api/products', {
        params: { page, pageSize }
      });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );
};

// Fetch all products across all pages
/**
 * The entire product catalogue.
 *
 * `enabled` because this is expensive - roughly 46 MB across a dozen paginated requests - and only one
 * flow needs it. It used to run on every bulk-update page load regardless.
 */
export const useAllProducts = (enabled: boolean = true) => {
  return useQuery<Product[]>(
    ['allProducts'],
    async () => {
      try {
        const allProducts: Product[] = [];        let page = 0;
        let hasMore = true;          while (hasMore) {
          const response = await apiClient.get<ProductsResponse>('/api/products', {
            params: { page, pageSize: 100 }
          });
          
          allProducts.push(...response.data.products);
          
          // Check if we've reached the last page
          hasMore = page < response.data.pages - 1;
          page++;
          
          // Runaway guard. Note this is a real ceiling, not just a loop break: at 100 per page it allows
          // 5,000 products. While the page size argument was being ignored the API served its default 25,
          // which capped this at 1,250 - and the catalogue was already at ~1,075, eight pages from
          // silently returning a truncated list with nothing but a console warning.
          if (page > 50) {
            console.warn('Product pagination hit the page ceiling; the catalogue may be truncated');
            break;
          }        }
        
        return allProducts;
      } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
      }
    },    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      enabled,
      onError: (error) => {
        console.error('Failed to fetch products:', error);
      }
    }
  );
};

// Create a variant lookup map
export const createVariantMap = (products: Product[]): Map<string, VariantInfo> => {
  const variantMap = new Map<string, VariantInfo>();
  
  products.forEach(product => {
    product.variants.forEach(variant => {
      // Build human-readable variant description
      const options = variant.selectedOptions
        .filter(option => option.optionType.internalName !== 'No Option')
        .map(option => ({
          optionType: option.optionType.internalName,
          optionValue: option.optionValue.internalName
        }));
      
      // Create a descriptive string from options
      const optionDescriptions = options.map(opt => opt.optionValue).filter(val => val);
      const variantDescription = optionDescriptions.length > 0 
        ? optionDescriptions.join(', ') 
        : 'Standard';
      
      // Full description combines product name and variant details
      const fullDescription = optionDescriptions.length > 0
        ? `${product.name} (${variantDescription})`
        : product.name;
      
      variantMap.set(variant.variantId, {
        variantId: variant.variantId,
        productName: product.name,
        productInternalName: product.internalName,
        variantDescription,
        fullDescription,
        price: variant.price.amount,
        categories: product.categories,
        options
      });
    });  });
  
  return variantMap;
};

// Hook to get variant information by ID
export const useVariantInfo = (variantId: string) => {
  const { data: products } = useAllProducts();
  
  if (!products) return null;
  
  const variantMap = createVariantMap(products);
  return variantMap.get(variantId) || null;
};

// Hook to get multiple variant infos
export const useVariantInfoMap = () => {
  const { data: products, isLoading, error } = useAllProducts();
  
  const variantMap = products ? createVariantMap(products) : new Map<string, VariantInfo>();
  
  return {
    variantMap,
    isLoading,
    error
  };
};

// Hook for prioritized variant loading - loads only needed variants first, then all
export const usePrioritizedVariantInfo = (neededVariantIds: string[] = []) => {
  // First, try to get just the variants we need immediately
  const { data: priorityProducts, isLoading: isPriorityLoading } = useQuery<Product[]>(
    ['priority-products', neededVariantIds.slice(0, 20).sort()], // Sort for consistent caching
    async () => {
      if (neededVariantIds.length === 0) return [];
      
      // This would ideally be a specific endpoint that takes variant IDs
      // For now, we'll load the first few pages which usually contain most common products
      const priorityProducts: Product[] = [];      const maxPriorityPages = Math.min(5, Math.ceil(neededVariantIds.length / 20)); // Load up to 5 pages or as needed
        for (let page = 0; page < maxPriorityPages; page++) {        const response = await apiClient.get<ProductsResponse>('/api/products', {
          params: { page, pageSize: 100 }
        });
        priorityProducts.push(...response.data.products);
        
        // Check if we found most of our needed variants
        const foundVariants = priorityProducts.flatMap(p => p.variants.map(v => v.variantId));
        const foundCount = neededVariantIds.filter(id => foundVariants.includes(id)).length;
        
        // If we found most of what we need, stop early
        if (foundCount >= neededVariantIds.length * 0.9) { // Increased threshold to 90%
          break;
        }
      }
      
      return priorityProducts;
    },
    {
      enabled: neededVariantIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes - longer cache time
      refetchOnWindowFocus: false, // Avoid unnecessary refetches
    }
  );  // Background loading of all products (only if we don't have complete data and priority loading is done)
  const { data: allProducts, isLoading: isBackgroundLoading } = useQuery<Product[]>(
    ['all-products'],
    async () => {
      const allProducts: Product[] = [];
      let page = 0;
      let totalPages = 1;
      
      while (page < totalPages) {
        const response = await apiClient.get<ProductsResponse>('/api/products', {
          params: { page, pageSize: 100 }
        });
        
        allProducts.push(...response.data.products);
        totalPages = response.data.pages;
        page++;
        
        // Prevent infinite loops
        if (page > 100) break;
      }
      
      return allProducts;
    },
    {
      enabled: !!priorityProducts && !isPriorityLoading, // Only start background loading after priority is done
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 60 * 60 * 1000, // 1 hour - very long cache time for complete data
      refetchOnWindowFocus: false,
    }
  );
    // Create variant maps
  const priorityVariantMap = priorityProducts ? createVariantMap(priorityProducts) : new Map();
  const allVariantMap = allProducts ? createVariantMap(allProducts) : new Map();
  
  // Use complete map if available, otherwise use priority map
  // Only fall back to priority map if we don't have complete data
  const activeVariantMap = allProducts ? allVariantMap : priorityVariantMap;  
  
  const isLoading = isPriorityLoading && !priorityProducts;
  
  return {
    variantMap: activeVariantMap,
    isLoading,
    isBackgroundLoading,
    hasPriorityData: !!priorityProducts,
    hasCompleteData: !!allProducts,
    priorityLoadProgress: priorityProducts ? 
      neededVariantIds.filter(id => priorityVariantMap.has(id)).length / Math.max(neededVariantIds.length, 1) : 0,
    // Additional metrics for debugging/optimization
    totalVariantsLoaded: activeVariantMap.size,
    neededVariantsFound: neededVariantIds.filter(id => activeVariantMap.has(id)).length
  };
};
